import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../styles/admin.css";
import { API_BASE, REDIS_URL } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { Form, Spinner, Modal } from "react-bootstrap";
import {
  Upload,
  DocumentPdf,
  Success,
  Warning,
  Error as ErrorIcon,
  Download,
  Trash,
} from "../icons";
import Sidebar from "../layout/Sidebar";
import PageFeedback from "../layout/PageFeedback";

async function readJsonResponse(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return {
      message: text,
    };
  }
}

function getBackendErrorMessage(data, fallback) {
  return (
    data?.detail ||
    data?.message ||
    data?.error ||
    data?.errors?.[0]?.message ||
    fallback
  );
}

function AdminUploads({ searchSpace, setSearchSpace }) {
  const [files, setFiles] = useState([]);

  const [uploadStatus, setUploadStatus] = useState({
    type: "",
    message: "",
  });

  const [actionStatus, setActionStatus] = useState({
    type: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [docList, setDocList] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const invalid = selectedFiles.some((file) => file.type !== "application/pdf");

    if (invalid) {
      setUploadStatus({
        type: "error",
        message: "Only PDF files are allowed.",
      });
      setFiles([]);
      e.target.value = "";
      return;
    }

    if (selectedFiles.length > 10) {
      setUploadStatus({
        type: "error",
        message: "You can upload a maximum of 10 files at once.",
      });
      setFiles([]);
      e.target.value = "";
      return;
    }

    setFiles(selectedFiles);
    setUploadStatus({ type: "", message: "" });
  };

  const fetchDocList = useCallback(async () => {
    setDocLoading(true);
    setDocError("");

    try {
      const res = await fetchWithAuth(
        `${API_BASE}/ainstein/listFiles?searchSpace=${encodeURIComponent(searchSpace)}`
      );

      let data = null;

      try {
        data = await readJsonResponse(res);
      } catch {
        data = null;
      }

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error(
            getBackendErrorMessage(
              data,
              "File list endpoint not available on backend. Please contact your administrator."
            )
          );
        }

        throw new Error(
          getBackendErrorMessage(
            data,
            `Error fetching file list (status ${res.status}).`
          )
        );
      }

      setDocList(Array.isArray(data?.files) ? data.files : []);
    } catch (e) {
      console.warn("Cannot fetch file list:", e.message);

      setDocError(
        e?.message || "Missing authorization or failed to load document list."
      );

      setDocList([]);
    } finally {
      setDocLoading(false);
    }
  }, [searchSpace]);

  useEffect(() => {
    fetchDocList();
  }, [fetchDocList]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!files.length) return;

    setLoading(true);
    setUploadStatus({ type: "", message: "" });

    try {
      const formData = new FormData();

      files.forEach((file) => formData.append("files", file));
      formData.append("searchSpace", searchSpace);
      formData.append("redis_url", REDIS_URL);
      const res = await fetchWithAuth(`${API_BASE}/ainstein/uploadFiles`, {
        method: "POST",
        body: formData,
      });

      let data = null;

      if (!res.ok) {
        try {
          data = await readJsonResponse(res);
        } catch {
          data = null;
        }

        if (res.status === 404) {
          throw new Error(
            getBackendErrorMessage(
              data,
              "Upload endpoint not available on backend. Please contact your administrator."
            )
          );
        }

        throw new Error(
          getBackendErrorMessage(
            data,
            `Upload failed: ${res.status} ${res.statusText}`
          )
        );
      }

      setUploadStatus({
        type: "success",
        message: "Files uploaded successfully!",
      });

      setFiles([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Upload error:", err);

      setUploadStatus({
        type: "error",
        message: err?.message || "Error during upload.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFiles([]);
    setUploadStatus({ type: "", message: "" });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleModalShow = () => {
    setShowModal(true);
    setUploadStatus({ type: "", message: "" });
  };

  useEffect(() => {
    if (uploadStatus.type === "success") {
      fetchDocList();

      const timer = setTimeout(() => {
        setShowModal(false);
      }, 1000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [uploadStatus.type, fetchDocList]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfFiles = droppedFiles.filter(
      (file) => file.type === "application/pdf"
    );

    if (droppedFiles.length !== pdfFiles.length) {
      setUploadStatus({
        type: "error",
        message: "Only PDF files are allowed.",
      });
      return;
    }

    if (pdfFiles.length > 10) {
      setUploadStatus({
        type: "error",
        message: "You can upload a maximum of 10 files at once.",
      });
      return;
    }

    setFiles(pdfFiles);
    setUploadStatus({ type: "", message: "" });
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const buildDownloadUrl = (filePath) => {
    const trimmed = (filePath || "").replace(/^\/+/, "");
    const encoded = trimmed.split("/").map(encodeURIComponent).join("/");
    return `${API_BASE}/ainstein/files/${encoded}`;
  };

  const handleDownload = async (filename) => {
    setActionStatus({ type: "", message: "" });

    try {
      const filePath = `/${searchSpace}/${filename}`;
      const url = buildDownloadUrl(filePath);

      const res = await fetchWithAuth(url, {
        method: "GET",
        headers: { Accept: "application/octet-stream, */*" },
      });

      if (!res.ok) {
        let data = null;

        try {
          data = await readJsonResponse(res);
        } catch {
          data = null;
        }

        throw new Error(
          getBackendErrorMessage(
            data,
            `Download failed: ${res.status} ${res.statusText}`
          )
        );
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = objectUrl;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("Download failed:", e);

      setActionStatus({
        type: "error",
        message:
          e?.message ||
          "File download failed. Please try again or contact support.",
      });
    }
  };

  const handleDelete = async (filename) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${filename}"? This will remove it from both the interface and vector storage.`
      )
    ) {
      return;
    }

    setActionStatus({ type: "", message: "" });

    try {
      const res = await fetchWithAuth(
        `${API_BASE}/ainstein/delete-file/?search_space=${encodeURIComponent(
          searchSpace
        )}&filename=${encodeURIComponent(filename)}&redis_url=${encodeURIComponent(
          REDIS_URL
        )}`,
        { method: "GET" }
      );

      let data = null;

      if (!res.ok) {
        try {
          data = await readJsonResponse(res);
        } catch {
          data = null;
        }

        throw new Error(
          getBackendErrorMessage(
            data,
            `Delete failed: ${res.status} ${res.statusText}`
          )
        );
      }

      setActionStatus({
        type: "success",
        message: `"${filename}" deleted successfully.`,
      });

      fetchDocList();
    } catch (err) {
      console.error("Delete error:", err);

      setActionStatus({
        type: "error",
        message: err?.message || "Failed to delete file.",
      });
    }
  };

  return (
    <div className="admin-shell-layout">
      <div className="admin-page-main">
        <div className="admin-page-inner">
          <div className="admin-header-row">
            <h2 className="admin-title">Manage Documents</h2>
            <button
              type="button"
              onClick={handleModalShow}
              className="admin-upload-btn"
              style={{ borderRadius: "32px" }}
            >
              <Upload
                width={24}
                height={24}
                fill="#FFFFFF"
                style={{ marginRight: 8, fill: "#FFFFFF" }}
              />
              Upload
            </button>
          </div>

          <Modal show={showModal} onHide={handleModalClose} centered>
            <Modal.Header closeButton>
              <Modal.Title>Upload file(s)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form className="admin-upload-form" onSubmit={handleSubmit}>
                <Form.Group controlId="searchSpaceSelect">
                  <Form.Label>Search Space:</Form.Label>
                  <Form.Control
                    type="text"
                    value={searchSpace || ""}
                    onChange={(e) => setSearchSpace(e.target.value)}
                    placeholder="Enter search space name"
                  />
                </Form.Group>

                <Form.Group controlId="fileUpload" className="mt-3">
                  <div
                    className={`file-drop-zone ${isDragging ? "dragging" : ""}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleDropZoneClick}
                  >
                    <DocumentPdf width={48} height={48} style={{ opacity: 0.5 }} />
                    <p className="drop-zone-text">
                      Drag and drop PDF files here or
                    </p>
                    <label
                      htmlFor="file-upload-input"
                      className="custom-file-upload"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Choose files
                    </label>
                    <Form.Control
                      ref={fileInputRef}
                      id="file-upload-input"
                      type="file"
                      accept="application/pdf"
                      multiple
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </div>

                  {files.length > 0 && (
                    <div className="selected-files-info">
                      <span>
                        {files.length} file{files.length > 1 ? "s" : ""} selected:
                      </span>
                      <ul className="selected-files-list">
                        {files.map((file, index) => (
                          <li key={index}>{file.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Form.Group>

                <button
                  className="admin-modal-upload-btn mt-3"
                  type="submit"
                  disabled={!files.length || loading}
                  style={{ borderRadius: "32px" }}
                >
                  {loading ? (
                    <>
                      <Upload
                        width={20}
                        height={20}
                        fill="#FFFFFF"
                        style={{
                          marginRight: 6,
                          marginBottom: 2,
                          fill: "#FFFFFF",
                        }}
                      />
                      <Spinner animation="border" size="sm" className="me-2" />
                      Uploading ...
                    </>
                  ) : (
                    <>
                      <Upload
                        width={20}
                        height={20}
                        fill="#FFFFFF"
                        style={{
                          marginRight: 6,
                          marginBottom: 2,
                          fill: "#FFFFFF",
                        }}
                      />
                      Upload
                    </>
                  )}
                </button>
              </Form>

              {uploadStatus.message && (
                <PageFeedback
                  type={uploadStatus.type || "info"}
                  className="admin-modal-feedback"
                >
                  {uploadStatus.message}
                </PageFeedback>
              )}
            </Modal.Body>
          </Modal>

          <div className="admin-files-section-wide">
            <div className="admin-files-header-row">
              <span className="admin-files-header">
                Documents in space: {searchSpace}
              </span>
            </div>

            {actionStatus.message && (
              <PageFeedback type={actionStatus.type || "info"}>
                {actionStatus.message}
              </PageFeedback>
            )}

            {docLoading ? (
              <PageFeedback type="info">Loading files ...</PageFeedback>
            ) : docError ? (
              <PageFeedback type="error">{docError}</PageFeedback>
            ) : docList.length === 0 ? (
              <div className="admin-files-empty">No documents found.</div>
            ) : (
              <table className="admin-files-table-wide">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {docList.map(({ filename, status }) => (
                    <tr key={filename}>
                      <td style={{ fontWeight: 500 }}>{filename}</td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            className={`se-badge-standalone se-badge-${status}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              minWidth: 66,
                              height: 28,
                              borderRadius: 24,
                              padding: "2px 0",
                            }}
                          >
                            <span
                              className="se-badge-content"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                                padding: "4px 8px",
                                height: 24,
                                borderRadius: 24,
                                background:
                                  status === "Wait"
                                    ? "#E2D000"
                                    : status === "InProgress"
                                      ? "#DA1E28"
                                      : "#007942",
                                border: `1px solid ${status === "Wait"
                                    ? "#E2D000"
                                    : status === "InProgress"
                                      ? "#DA1E28"
                                      : "#007942"
                                  }`,
                              }}
                            >
                              {status === "Wait" && (
                                <ErrorIcon
                                  width={16}
                                  height={16}
                                  style={{
                                    background: "#ffffff",
                                    borderRadius: 8,
                                  }}
                                />
                              )}

                              {status === "InProgress" && (
                                <Warning
                                  width={16}
                                  height={16}
                                  style={{
                                    background: "#ffffff",
                                    borderRadius: 8,
                                  }}
                                />
                              )}

                              {status === "Completed" && (
                                <Success
                                  width={16}
                                  height={16}
                                  style={{
                                    background: "#ffffff",
                                    borderRadius: 8,
                                  }}
                                />
                              )}

                              <span
                                className="se-badge-label"
                                style={{
                                  fontFamily: "Siemens Sans",
                                  fontWeight: 700,
                                  fontSize: 10,
                                  lineHeight: "14px",
                                  letterSpacing: 0.2,
                                  display: "flex",
                                  alignItems: "center",
                                  textAlign: "center",
                                  padding: "0 2px",
                                  color: status === "Wait" ? "#121212" : "#fff",
                                  height: 14,
                                }}
                              >
                                {status === "Wait"
                                  ? "Waiting"
                                  : status === "InProgress"
                                    ? "In Progress"
                                    : status === "Completed"
                                      ? "Completed"
                                      : status}
                              </span>
                            </span>
                          </span>

                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <button
                              onClick={() => handleDownload(filename)}
                              title="Download file"
                              className="admin-file-action-btn"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "opacity 0.2s",
                              }}
                              ref={(btn) => {
                                if (btn) {
                                  const icon = btn.querySelector("svg");
                                  if (icon) {
                                    const paths = icon.querySelectorAll("path");
                                    paths.forEach((path) => {
                                      path.setAttribute("fill", "#FFFFFF");
                                    });
                                  }
                                }
                              }}
                              onMouseEnter={(e) => {
                                const icon = e.currentTarget.querySelector("svg");
                                if (icon) {
                                  const paths = icon.querySelectorAll("path");
                                  paths.forEach((path) => {
                                    path.setAttribute("fill", "#E0E0E0");
                                  });
                                }
                              }}
                              onMouseLeave={(e) => {
                                const icon = e.currentTarget.querySelector("svg");
                                if (icon) {
                                  const paths = icon.querySelectorAll("path");
                                  paths.forEach((path) => {
                                    path.setAttribute("fill", "#FFFFFF");
                                  });
                                }
                              }}
                            >
                              <Download width={20} height={20} />
                            </button>

                            <button
                              onClick={() => handleDelete(filename)}
                              title="Delete file"
                              className="admin-file-action-btn"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "opacity 0.2s",
                                color: "#DA1E28",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#B71C1C";
                                const icon = e.currentTarget.querySelector("svg");
                                if (icon) {
                                  icon.style.stroke = "#B71C1C";
                                  icon.style.fill = "none";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#DA1E28";
                                const icon = e.currentTarget.querySelector("svg");
                                if (icon) {
                                  icon.style.stroke = "#DA1E28";
                                  icon.style.fill = "none";
                                }
                              }}
                            >
                              <Trash
                                width={20}
                                height={20}
                                stroke="#DA1E28"
                                fill="none"
                                strokeWidth="2"
                              />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminUploads;